import React, { useEffect, useState } from 'react'
import { FaTrophy, FaChartLine, FaFire, FaCheckCircle } from 'react-icons/fa'
import useUser from '../Hooks/useUser'
import { db } from '../../firebase'
import { collection, getDocs } from 'firebase/firestore'
import LoadingSnipper from '../Common/LoadingSnipper'

function ProgressSection() {
    const userState = useUser()
    const [quizzesTaken, setQuizzesTaken] = useState([])
    const [statisticsStatus, setStatisticsStatus] = useState('loading')

    useEffect(() => {
        const fetchUserProgress = async () => {
            if (!userState.isSignedIn || !userState.userId) {
                setStatisticsStatus('success')
                return
            }

            try {
                setStatisticsStatus('loading')
                const docRef = collection(db, userState.userId)
                const quizSnapshots = await getDocs(docRef)

                const quizzesData = quizSnapshots.docs.map(docSnapshot => {
                    const quiz = docSnapshot.data()
                    const scores = quiz.score || []
                    const maxScore = quiz.numbQuestions || 1
                    const totalScore = scores.reduce((sum, score) => sum + score, 0)
                    const averageScore = scores.length > 0 ? totalScore / scores.length : 0
                    const highestScore = scores.length > 0 ? Math.max(...scores) : 0
                    const bestScore = Math.round((highestScore / maxScore) * 100)
                    const quizAttemps = scores.length
                    const rawPercentage = (averageScore / maxScore) * 100
                    const percentage = Math.round(rawPercentage)
                    const mostRecentScore = scores.length > 0 ? scores[scores.length - 1] : 0
                    const recentScorePercentage = Math.round((mostRecentScore / maxScore) * 100)

                    return {
                        id: docSnapshot.id,
                        data: {
                            ...quiz,
                            percentage,
                            bestScore,
                            quizAttemps,
                            recentScorePercentage,
                        }
                    }
                })

                setQuizzesTaken(quizzesData)
                setStatisticsStatus('success')
            } catch (error) {
                console.error(error)
                setStatisticsStatus('error')
            }
        }

        fetchUserProgress()
    }, [userState.isSignedIn, userState.userId])

    if (!userState.isSignedIn) {
        return (
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border-2 border-purple-200">
                    <FaChartLine className="text-5xl text-purple-600 mx-auto mb-4" />
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        Acompanhe seu Progresso
                    </h2>
                    <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                        Faça login para acompanhar suas estatísticas de aprendizado, ver seus quizzes completados e acompanhar seu progresso!
                    </p>
                    <a
                        href="/login"
                        className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                    >
                        Fazer Login
                    </a>
                </div>
            </div>
        )
    }

    if (statisticsStatus === 'loading') {
        return <LoadingSnipper />
    }

    const totalQuizzes = quizzesTaken?.length || 0
    const totalAttempts = quizzesTaken?.reduce((sum, quiz) => sum + (quiz.data.quizAttemps || 0), 0) || 0
    const averageScore = quizzesTaken?.length > 0
        ? Math.round(quizzesTaken.reduce((sum, quiz) => sum + (quiz.data.percentage || 0), 0) / quizzesTaken.length)
        : 0
    const bestScore = quizzesTaken?.length > 0
        ? Math.max(...quizzesTaken.map(quiz => quiz.data.bestScore || 0))
        : 0

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <FaChartLine className="text-3xl text-purple-600" />
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                        Meu Progresso
                    </h2>
                </div>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    Acompanhe seu desempenho e veja como você está evoluindo
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-purple-100 text-center">
                    <FaTrophy className="text-4xl text-yellow-500 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-gray-900 mb-1">{bestScore}%</div>
                    <div className="text-sm text-gray-600">Melhor Pontuação</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-green-100 text-center">
                    <FaCheckCircle className="text-4xl text-green-500 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-gray-900 mb-1">{totalQuizzes}</div>
                    <div className="text-sm text-gray-600">Quizzes Completados</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-blue-100 text-center">
                    <FaChartLine className="text-4xl text-blue-500 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-gray-900 mb-1">{averageScore}%</div>
                    <div className="text-sm text-gray-600">Média de Acertos</div>
                </div>

                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-orange-100 text-center">
                    <FaFire className="text-4xl text-orange-500 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-gray-900 mb-1">{totalAttempts}</div>
                    <div className="text-sm text-gray-600">Tentativas Totais</div>
                </div>
            </div>

            {/* Recent Quizzes */}
            {quizzesTaken && quizzesTaken.length > 0 ? (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-lg border-2 border-purple-100">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Quizzes Recentes</h3>
                    <div className="space-y-4">
                        {quizzesTaken.slice(0, 5).map((quiz) => (
                            <div
                                key={quiz.id}
                                className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-100"
                            >
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900">{quiz.data.title || 'Quiz'}</h4>
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                        <span>Última: {quiz.data.recentScorePercentage || 0}%</span>
                                        <span>Melhor: {quiz.data.bestScore || 0}%</span>
                                        <span>Tentativas: {quiz.data.quizAttemps || 0}</span>
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold ${
                                        (quiz.data.recentScorePercentage || 0) >= 80 ? 'bg-green-500 text-white' :
                                        (quiz.data.recentScorePercentage || 0) >= 60 ? 'bg-yellow-500 text-white' :
                                        'bg-red-500 text-white'
                                    }`}>
                                        {quiz.data.recentScorePercentage || 0}%
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {quizzesTaken.length > 5 && (
                        <div className="mt-6 text-center">
                            <a
                                href={`/my-progress/${userState.userId}`}
                                className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-300"
                            >
                                Ver Todos os Quizzes
                            </a>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white/80 backdrop-blur-sm rounded-xl p-12 shadow-lg border-2 border-purple-100 text-center">
                    <FaChartLine className="text-5xl text-gray-400 mx-auto mb-4" />
                    <p className="text-xl text-gray-600 mb-2">Nenhum quiz completado ainda</p>
                    <p className="text-gray-500">Complete alguns quizzes para ver suas estatísticas aqui!</p>
                </div>
            )}

            {/* Motivational Message */}
            <div className="mt-8 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 text-center text-white shadow-xl">
                <FaFire className="text-5xl mx-auto mb-4 opacity-90" />
                <h3 className="text-2xl font-bold mb-2">Continue praticando!</h3>
                <p className="text-lg opacity-90 max-w-2xl mx-auto">
                    A prática constante é a chave para dominar o inglês. Continue fazendo exercícios e acompanhe seu progresso!
                </p>
            </div>
        </div>
    )
}

export default ProgressSection

